import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Clock,
  ChartLineUp,
  Users as UsersIcon,
  Storefront,
  CalendarBlank,
  ClipboardText,
  ChartBar,
  Briefcase,
  ShieldCheck,
  Gear,
  SignOut,
  List,
  X,
  Sparkle,
} from "@phosphor-icons/react";
import TimeTrackLogo from "./TimeTrackLogo";
import ProductSwitcher from "./ProductSwitcher";

const navByRole = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: ChartLineUp },
    { to: "/users", label: "Users", icon: UsersIcon },
    { to: "/stores", label: "Stores", icon: Storefront },
    { to: "/shifts", label: "Shifts", icon: CalendarBlank },
    { to: "/assignments", label: "Assignments", icon: Briefcase },
    { to: "/corrections", label: "Corrections", icon: ClipboardText },
    { to: "/reports", label: "Reports", icon: ChartBar },
    { to: "/leads", label: "Trial Leads", icon: Sparkle },
    { to: "/audit-log", label: "Audit Log", icon: ShieldCheck },
    { to: "/settings", label: "Settings", icon: Gear },
    { to: "/clock", label: "Clock In/Out", icon: Clock },
    { to: "/history", label: "My History", icon: ClipboardText },
  ],
  supervisor: [
    { to: "/dashboard", label: "Dashboard", icon: ChartLineUp },
    { to: "/assignments", label: "Assignments", icon: Briefcase },
    { to: "/corrections", label: "Corrections", icon: ClipboardText },
    { to: "/reports", label: "Reports", icon: ChartBar },
    { to: "/clock", label: "Clock In/Out", icon: Clock },
    { to: "/history", label: "My History", icon: ClipboardText },
  ],
  employee: [
    { to: "/clock", label: "Clock In/Out", icon: Clock },
    { to: "/history", label: "History", icon: ClipboardText },
    { to: "/corrections", label: "Corrections", icon: ClipboardText },
  ],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const items = navByRole[user.role] || [];

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex flex-col w-64 border-r border-zinc-200 bg-white sticky top-0 h-screen">
        <Brand />
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {items.map((it) => (
            <NavItem key={it.to} {...it} />
          ))}
        </nav>
        <UserCard user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="flex items-center justify-between px-4 py-3">
          <TimeTrackLogo size={30} variant="lockup" showParent={false} />
          <div className="flex items-center gap-2">
            <ProductSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="p-2 text-zinc-700"
            >
              {open ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="border-t border-zinc-200 bg-white px-3 py-2">
            {items.map((it) => (
              <NavItem key={it.to} {...it} onClick={() => setOpen(false)} />
            ))}
            <button
              data-testid="mobile-logout-btn"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-sm"
            >
              <SignOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      <main className="flex-1 md:pl-0 pt-16 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}

function Brand() {
  return (
    <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between gap-2">
      <TimeTrackLogo size={36} variant="lockup" showParent={false} />
      <ProductSwitcher />
    </div>
  );
}

function NavItem({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${to.replace("/", "")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
          isActive
            ? "bg-zinc-950 text-white"
            : "text-zinc-700 hover:bg-zinc-100"
        }`
      }
    >
      <Icon size={16} weight="bold" />
      <span>{label}</span>
    </NavLink>
  );
}

function UserCard({ user, onLogout }) {
  return (
    <div className="border-t border-zinc-200 px-3 py-3 space-y-2">
      <div className="px-2 py-1">
        <div className="text-xs label-eyebrow">{user.role}</div>
        <div data-testid="sidebar-user-name" className="text-sm font-semibold text-zinc-950 truncate">{user.name}</div>
        <div className="text-xs text-zinc-500 truncate">{user.email}</div>
      </div>
      <button
        data-testid="logout-btn"
        onClick={onLogout}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-sm transition-colors"
      >
        <SignOut size={16} />
        <span>Sign Out</span>
      </button>
    </div>
  );
}
